import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiCall } from '../AuthContext';
import { X, Info, AlertTriangle, CheckCircle2, Sparkles } from 'lucide-react';

const VARIANT_STYLES = {
  info: { bg: 'bg-blue-50 border-blue-200 text-blue-900', icon: Info, iconCls: 'text-blue-600' },
  warning: { bg: 'bg-amber-50 border-amber-300 text-amber-900', icon: AlertTriangle, iconCls: 'text-amber-700' },
  success: { bg: 'bg-emerald-50 border-emerald-200 text-emerald-900', icon: CheckCircle2, iconCls: 'text-emerald-700' },
  promo: { bg: 'bg-purple-50 border-purple-200 text-purple-900', icon: Sparkles, iconCls: 'text-purple-700' },
};

/**
 * Reads /api/announcements (cached for the session) and renders any enabled
 * announcement matching the current tab/path. Honours user-side dismissal via localStorage.
 *
 * Tab id is derived from the leading path segment (e.g. /dashboard -> "dashboard").
 */
export default function AnnouncementBanner() {
  const location = useLocation();
  const [announcements, setAnnouncements] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('xlabel_dismissed_announcements') || '{}'); } catch { return {}; }
  });

  useEffect(() => {
    let cancelled = false;
    apiCall('/api/announcements', { method: 'GET' })
      .then(r => { if (!cancelled) setAnnouncements(r.data?.announcements || []); })
      .catch(() => { /* noop */ });
    return () => { cancelled = true; };
  }, []);

  const tab = (location.pathname || '/').split('/').filter(Boolean)[0] || '';
  if (!tab) return null;

  const ann = announcements.find(a => a.tab === tab && a.enabled);
  if (!ann) return null;

  const dismissKey = `${ann.tab}-${ann.updated_at || ''}`;
  if (dismissed[dismissKey]) return null;

  const v = VARIANT_STYLES[ann.variant] || VARIANT_STYLES.info;
  const Icon = v.icon;

  const dismiss = () => {
    const next = { ...dismissed, [dismissKey]: true };
    setDismissed(next);
    localStorage.setItem('xlabel_dismissed_announcements', JSON.stringify(next));
  };

  return (
    <div data-testid={`announcement-banner-${ann.tab}`} className={`border rounded-xl px-4 py-3 ${v.bg} flex items-start gap-3 mb-6 shadow-sm`}>
      <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${v.iconCls}`} />
      <div className="flex-1 min-w-0">
        {ann.title && <p className="text-sm font-bold mb-0.5">{ann.title}</p>}
        <div className="text-sm prose prose-sm max-w-none [&>p]:m-0 [&_a]:underline" dangerouslySetInnerHTML={{ __html: ann.content_html || '' }} />
      </div>
      {ann.dismissible && (
        <button onClick={dismiss} className="flex-shrink-0 p-1 rounded hover:bg-black/5 opacity-60 hover:opacity-100" data-testid={`dismiss-announcement-${ann.tab}`}>
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
